import * as React from 'react';

import "./App.css";

import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ButtonGroup from '@mui/material/ButtonGroup';

import { useTheme } from '@mui/material/styles';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import PrintIcon from '@mui/icons-material/Print';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import LinkIcon from '@mui/icons-material/Link';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RuleIcon from '@mui/icons-material/Rule';
import TranslateIcon from '@mui/icons-material/Translate';

import { JsonForms } from '@jsonforms/react';
import {
	materialRenderers,
	materialCells,
} from '@jsonforms/material-renderers';

import schemaJson from './finalthesis.schema.json';
import uischemaJson from './finalthesis.uischema.json';
import examiners from './examiners.json';
import * as Form from './finalthesis';
import * as Data from './DataRepresentation';
import * as PdfForm from './PdfForm';
import HelpDialog from './HelpDialog';
import ValidationDialog from './ValidationDialog';
import { ErrorObject } from 'ajv';

import ScrollToTop from './ScrollToTop';
import Fab from '@mui/material/Fab';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {getDebug} from "./Data";
import {T, useTolgee, useTranslate} from "@tolgee/react";
import {HTML_TAGS} from "./I18n";
import {JsonSchema7, Translator} from "@jsonforms/core";
import {Grid2} from "@mui/material";


const jsonFormsConfig = {
	restrict: false,
	trim: false,
	showUnfocusedDescription: true,
	hideRequiredAsterisk: false
}

const DOWNLOAD = 'download';

const PRINT = 'print';

// Define a type for the component props
interface AppProps {
}

// Use the 'FC' (Functional Component) generic from React, with our props type
const FinalThesisApp: React.FC<AppProps> = (_props: AppProps) => {
	const [formData, setFormData] = React.useState<Form.AnmeldungDerAbschlussarbeit>(Data.getInitialFormData());
	const [errors, setErrors] = React.useState<ErrorObject[]>([]);
	const [snackbarOpen, setSnackbarOpen] = React.useState<boolean>(false);
	const [warningDialogOpen, setWarningDialogOpen] = React.useState<boolean>(false);
	const [warningMessage, setWarningMessage] = React.useState<string>("");
	const [warningContinueAction, setWarningContinueAction] = React.useState('');
	const [helpDialogOpen, setHelpDialogOpen] = React.useState(false);
	const [validationDialogOpen, setValidationDialogOpen] = React.useState(false);
	const [customValidationErrors, setCustomValidationErrors] = React.useState<ErrorObject[]>([]);

	const locale = Data.getInitialLang();
	
	const SUPERVISOR_OTHER = "Andere(r)";

	schemaJson.properties.supervisor.enum = [...examiners.map(examiner => examiner.sortName), SUPERVISOR_OTHER];
	schemaJson.properties.second_supervisor.enum = [...examiners.map(examiner => examiner.sortName), SUPERVISOR_OTHER];

	const jsonformsTranslate = ((key: string, defaultMessage: string | undefined = undefined, _context: any = undefined): string | undefined => {
		if (key.indexOf("error.") >= 0) {
			return defaultMessage;
		}
		if (key.startsWith("supervisor.") || key.startsWith("second_supervisor.")) {
			const examinerCandidate = key.substring(key.indexOf(".") + 1);
			const translated = examiners.find(examiner => examiner.displayName === examinerCandidate)?.displayName;
			if (translated !== undefined)
				return translated;
		}
		return t(key);
	}) as Translator;

	const jsonformsErrorTranslate = ((error: ErrorObject, _translate?: Translator) => {
			return t("ajv_error." + error.keyword + (error.keyword === "pattern" ? error.instancePath : ""), error.message, error.params);
	});

	const {t} = useTranslate();
	const tolgee = useTolgee();
	localStorage.setItem(Data.LOCAL_STORE_KEY_FORM_DATA, JSON.stringify(formData));
	localStorage.setItem(Data.LOCAL_STORE_KEY_LANG, locale);

	const onDownloadDraft = () => {
		const degree = formData.degree ? formData.degree : "degree";
		const lastname = formData.lastname ? formData.lastname : "lastname";
		const firstname = formData.firstname ? formData.firstname : "firstname";

		const filename = degree + '_' + lastname.trim().replaceAll(' ', '-') + '_' + firstname.trim().replaceAll(' ', '-') + '_draft.pdf';

		const dd = PdfForm.pdfFormDocumentDefinition(formData, false, t, locale);
		PdfForm.download(dd, filename);
	};

	const onPrintDraft = () => {
		const dd = PdfForm.pdfFormDocumentDefinition(formData, false, t, locale);
		PdfForm.print(dd);
	};

	const downloadFinal = () => {
		const degree = formData.degree ? formData.degree : "degree";
		const lastname = formData.lastname ? formData.lastname : "lastname";
		const firstname = formData.firstname ? formData.firstname : "firstname";

		const filename = degree + '_' + lastname.trim().replaceAll(' ', '-') + '_' + firstname.trim().replaceAll(' ', '-') + '.pdf';

		const dd = PdfForm.pdfFormDocumentDefinition(formData, true, t, locale);
		PdfForm.download(dd, filename);
	}

	const onDownloadFinal = () => {
		if (errors.length > 0 || customValidationErrors.length > 0) {
			setWarningMessage(t("page.warning.download_on_incomplete"));
			setWarningContinueAction(DOWNLOAD);
			setWarningDialogOpen(true);
		} else {
			downloadFinal()
		}
	};

	const printFinal = () => {
		const dd = PdfForm.pdfFormDocumentDefinition(formData, true, t, locale);
		PdfForm.print(dd);
	}

	const onPrintFinal = () => {
		if (errors.length > 0 || customValidationErrors.length > 0) {
			setWarningMessage(t("page.warning.print_on_incomplete"));
			setWarningContinueAction(PRINT);
			setWarningDialogOpen(true);
		} else {
			printFinal();
		}
	};

	const handleCloseWarningDialog = () => {
		setWarningDialogOpen(false);
	}

	const handleContinue = () => {
		handleCloseWarningDialog();
		if (warningContinueAction === DOWNLOAD) {
			downloadFinal();
		} else if (warningContinueAction === PRINT) {
			printFinal();
		}
	}

	const removeRequiredFromBooleans = (schema: JsonSchema7) : JsonSchema7 => {
		if (schema.required !== undefined) {
			let newProperties = {...schema.properties};
			if (schema.properties !== undefined) {
				const propNames = Object.keys(schema.properties);
				propNames.forEach(name => {
					if (schema.properties !== undefined && schema.properties[name] !== undefined) {
						const newProperty = removeRequiredFromBooleans(schema.properties[name]);
						if (newProperty !== undefined)
							newProperties[name] = newProperty;
					}});
			}
			return {
				...schema,
				properties: newProperties,
				required: schema.required.filter(propName => {
					if (schema.properties !== undefined) {
						const prop = schema.properties[propName];
						if (prop !== undefined && prop.type === "boolean") {
							return false;
						}
					}
					return true;
				})
			};
		}
		return schema;
	}

	React.useEffect(() => {
		const customErrors: ErrorObject[] = [];
		const today = new Date(new Date().toDateString());
		const eighteenYearsAgo = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
		
		//^[\u0020-\u007e\u00a0-\u00ff]+$

		const checkStringProperties = (metaObject : JsonSchema7, data: { [x: string]: any; } | any, path: string) => {
			if (metaObject.type === 'string' && data !== undefined) {
					const str = data as string;
					let message = '';
					for (let i = 0; i < str.length; i++) {
						// legal characters for iso 8859-1 (Latin-1) encoding, as requuired for QR code
						//\u0020-\u007e\u00a0-\u00ff
  						const c = str.charAt(i);
  						if (!(
							  (c >= '\u0020' && c <= '\u007e') ||
							  (c >= '\u00a0' && c <= '\u00ff')
							  )) {
								  if (message !=='') {
									  message = message + ' ';
								  }

								  message += t('invalid.allowed_char_at_index',
								  	{
										index: i.toString(),
								  		character: c.toString()
								  	});
						  }
					}
					if (message !== '') {
						customErrors.push({
							// AJV style path to the property in the schema
							instancePath: path,
							// message to display
							message: message + ' ' + t('invalid.allowed_char_explanation'),
							schemaPath: '',
							keyword: '',
							params: {},
						});
					}
			}
			if (metaObject.properties !== undefined) {
				for (const key in metaObject.properties) {
					const subMetaObject = metaObject.properties[key];
					checkStringProperties(subMetaObject, data[key], path + '/' + key);
				}
			}
		}

	checkStringProperties(schemaJson, formData, '');

		if (formData.birthday !== undefined && new Date(formData.birthday) > today) {
			customErrors.push({
				// AJV style path to the property in the schema
				instancePath: '/birthday',
				// message to display
				message: 'invalid.birthday_in_past',
				schemaPath: '',
				keyword: 'birthday_in_past',
				params: {},
			});
		} else if (formData.birthday !== undefined && new Date(formData.birthday) > eighteenYearsAgo) {
			customErrors.push({
				// AJV style path to the property in the schema
				instancePath: '/birthday',
				// message to display
				message: 'invalid.older_than_18',
				schemaPath: '',
				keyword: 'older_than_18',
				params: {},
			});
		}

		if (formData.start_date !== undefined && new Date(formData.start_date) < today) {
			customErrors.push({
				// AJV style path to the property in the schema
				instancePath: '/start_date',
				// message to display
				message: 'invalid.start_date_in_future',
				schemaPath: '',
				keyword: 'start_date_in_future',
				params: {},
			});
		}

		let startDateLimit = new Date(today);
		startDateLimit.setDate(startDateLimit.getDate() + 60);
		if (formData.start_date !== undefined && new Date(formData.start_date) > startDateLimit) {
			customErrors.push({
				// AJV style path to the property in the schema
				instancePath: '/start_date',
				// message to display
				message: 'invalid.start_date_soon',
				schemaPath: '',
				keyword: 'start_date_soon',
				params: {},
			});
		}

		if (formData.supervisor === SUPERVISOR_OTHER && formData.supervisor_other === undefined) {
			customErrors.push({
				// AJV style path to the property in the schema
				instancePath: '/supervisor_other',
				// message to display
				message: 'invalid.required_supervisor_other',
				schemaPath: '',
				keyword: 'required_supervisor_other',
				params: {},
			});
		}

		if (formData.second_supervisor === SUPERVISOR_OTHER && formData.second_supervisor_other === undefined) {
			customErrors.push({
				// AJV style path to the property in the schema
				instancePath: '/second_supervisor_other',
				// message to display
				message: 'invalid.required_second_supervisor_other',
				schemaPath: '',
				keyword: 'required_second_supervisor_other',
				params: {},
			});
		}

		const supervisor = formData.supervisor === SUPERVISOR_OTHER ? formData.supervisor_other : formData.supervisor;
		const second_supervisor = formData.second_supervisor === SUPERVISOR_OTHER ? formData.second_supervisor_other : formData.second_supervisor;
		if (supervisor !== undefined && supervisor === second_supervisor) {
			customErrors.push({
				// AJV style path to the property in the schema
				instancePath: formData.supervisor !== SUPERVISOR_OTHER ? '/supervisor' : '/supervisor_other',
				// message to display
				message: 'invalid.distinct_supervisor_and_reviewer',
				schemaPath: '',
				keyword: 'distinct_supervisor_and_reviewer',
				params: {},
			});
			customErrors.push({
				// AJV style path to the property in the schema
				instancePath: formData.second_supervisor !== SUPERVISOR_OTHER ? '/second_supervisor' : '/second_supervisor_other',
				// message to display
				message: 'invalid.distinct_supervisor_and_reviewer',
				schemaPath: '',
				keyword: 'distinct_supervisor_and_reviewer',
				params: {},
			});
		}

		if (formData.exam_regulations === "Andere" && formData.exam_regulations_other === undefined) {
			customErrors.push({
				// AJV style path to the property in the schema
				instancePath: '/exam_regulations_other',
				// message to display
				message: 'invalid.required_exam_regulations_other',
				schemaPath: '',
				keyword: 'required_exam_regulations_other',
				params: {},
			});
		}

		const requirementsPropertyName = Data.getRequirementsPropertyName(formData);

		if (requirementsPropertyName === undefined || formData[requirementsPropertyName as Data.FormKey] === undefined) {
			// studies not fully specified
			const requirementOther = formData.requirements_other;
			if (requirementOther === undefined || requirementOther.fulfilled !== true) {
				customErrors.push({
					// AJV style path to the property in the schema
					instancePath: '/requirements_other/fulfilled',
					// message to display
					message: 'invalid.requirements_other_fulfilled',
					schemaPath: '',
					keyword: 'requirements_other_fulfilled',
					params: {},
				});
			}
			if (requirementOther === undefined || requirementOther.description === undefined || requirementOther.description === "") {
				customErrors.push({
					// AJV style path to the property in the schema
					instancePath: '/requirements_other/description',
					// message to display
					message: 'invalid.required_requirements_other_description',
					schemaPath: '',
					keyword: 'required_requirements_other_description',
					params: {},
				});
			}

		} else {

			const requirements = formData[requirementsPropertyName as Data.FormKey] as Form.Requirements;
			const schema = schemaJson as JsonSchema7;
			if (schema.properties !== undefined) {
				const requirementPropertiesMO = schema.properties[requirementsPropertyName].properties;

				for (const key in requirementPropertiesMO) {
					if (schema.properties[requirementsPropertyName].required?.includes(key) &&
							requirementPropertiesMO !== undefined &&
							requirementPropertiesMO[key].type === "boolean" &&
							requirements[key] !== true) {
						customErrors.push({
							// AJV style path to the property in the schema
							instancePath: '/' + requirementsPropertyName + '/' + key,
							// message to display
							message: 'invalid.requirement_fulfilled',
							schemaPath: '',
							keyword: 'requirement_fulfilled',
							params: {},
						});
						console.log('/' + requirementsPropertyName + '/' + key);
					}
				}
			} else {
				throw new Error('Illegal Schema.');
			}
		}


		setCustomValidationErrors(customErrors);
	}, [formData]);

	const theme = useTheme();

	return (
		<React.Fragment>

			<Grid2
				container
				direction="row"
				sx={{
					justifyContent: "center",
					alignItems: "center",
				}}
			>
				<Box
					component="img"
					sx={{
						width: 9/10,
						maxWidth: { alignItems: 'center' }
					}}
					alt={t("aria.workflow-banner")}
					src="WorkflowBanner.png"
				/>
			</Grid2>
			<Box id="back-to-top-anchor">
				<Typography variant="body2">
					<T
						keyName="page.introduction"
						params={{
							...HTML_TAGS,
							help_icon: <HelpOutlineIcon aria-label={t("aria.help_icon")} style={{ verticalAlign: 'middle' }} />,
							print_icon: <PrintIcon aria-label={t("aria.print_icon")} style={{ verticalAlign: 'middle' }} />
						}}
					/>
				</Typography>
			</Box>
			<Stack spacing={2} direction="column">

				<Stack spacing={2} direction="row" justifyContent="flex-start" sx={{ flexWrap: 'wrap' }}>
					<ButtonGroup variant="contained" aria-label={t("aria.reset.buttongroup")}>
						<Button variant="contained"
							onClick={() => {
								setFormData(Data.EMPTY_FORM_DATA)
							}}
						>{t("page.reset")}</Button>
					</ButtonGroup>

					<ButtonGroup variant="contained" aria-label={t("aria.draft.buttongroup")}>
						<Button variant="contained"
							onClick={() => {
								navigator.clipboard.writeText(Data.editLink(formData, locale));
								setSnackbarOpen(true);
							}}
							startIcon={<LinkIcon style={{ color: theme.palette.primary.contrastText }} />}>
							{t("page.draft")}
							<Snackbar
								open={snackbarOpen}
								autoHideDuration={6000}
								onClose={() => {
									setSnackbarOpen(false);
								}}
							>
								<Alert
									severity="success"
									style={{ textTransform: 'none', color: 'white' }}
								>
									{t("page.link_copied")}
								</Alert>
							</Snackbar>
						</Button>
						<Button onClick={onDownloadDraft} aria-label={t("aria.download-draft")} startIcon={<FileDownloadIcon style={{ color: theme.palette.primary.contrastText }} />}> {t("page.draft")}</Button>
						<Button onClick={onPrintDraft} aria-label={t("aria.print-draft")} startIcon={<PrintIcon style={{ color: theme.palette.primary.contrastText }} />}>{t("page.draft")}</Button>
					</ButtonGroup>

					<ButtonGroup variant="contained" aria-label={t("aria.final.buttongroup")}>
						<Button onClick={onDownloadFinal} aria-label={t("aria.download-final")} startIcon={<FileDownloadIcon style={{ color: theme.palette.primary.contrastText }} />}> {t("page.final")}</Button>
						<Button onClick={onPrintFinal} aria-label={t("aria.print-final")} startIcon={<PrintIcon style={{ color: theme.palette.primary.contrastText }} />}>{t("page.final")}</Button>
					</ButtonGroup>
					<Button variant="contained"
						onClick={() => { setValidationDialogOpen(true) }}
					><RuleIcon /></Button>
					<Button variant="contained"
						onClick={() => { setHelpDialogOpen(true) }}
					><HelpOutlineIcon /></Button>

				</Stack>

				<Box
					sx={{
						borderRadius: 1,
						bgcolor: '#eee',
						p: 1,
					}}
				>
					<JsonForms
						schema={removeRequiredFromBooleans(schemaJson)}
						uischema={uischemaJson}
						data={formData}
						renderers={materialRenderers}
						cells={materialCells}
						config={jsonFormsConfig}
						onChange={({ data, errors }) => { setFormData(data); setErrors(errors as any[]); }}
						i18n={{
							translate: jsonformsTranslate,
							locale: locale,
							translateError: jsonformsErrorTranslate
						}}
						additionalErrors={customValidationErrors}
					/>
				</Box>
				<Typography variant="body2">
					<T
						keyName="page.submission_instructions"
						params={{
							...HTML_TAGS,
							help_icon: <HelpOutlineIcon aria-label={t("aria.help_icon")} style={{ verticalAlign: 'middle' }} />,
							print_icon: <PrintIcon aria-label={t("aria.print_icon")} style={{ verticalAlign: 'middle' }} />
						}}
					/>
				</Typography>
				<Typography variant="body2" align="right">{`${Data.FORM_NAME} | ${Data.FORM_VERSION} | ${Data.FORM_DATE}`}</Typography>
			</Stack>
			<Dialog
				open={warningDialogOpen}
				onClose={handleCloseWarningDialog}
				aria-labelledby="incomplete-dialog-title"
				aria-describedby="incomplete-dialog-description"
			>
				<DialogTitle id="incomplete-dialog-title">
					{t("page.dialog.incomplete_title")}
				</DialogTitle>
				<DialogContent>
					<DialogContentText id="incomplete-dialog-description">
						{warningMessage}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseWarningDialog}>{t("page.dialog.abort")}</Button>
					<Button onClick={handleContinue} autoFocus>
						{t("page.dialog.continue")}
					</Button>
				</DialogActions>
			</Dialog>
			<HelpDialog open={helpDialogOpen} setOpen={setHelpDialogOpen} formData={formData} />
			<ValidationDialog open={validationDialogOpen} setOpen={setValidationDialogOpen} errors={errors.concat(customValidationErrors)} />
			<ScrollToTop>
				<Fab size="small" aria-label={t("aria.scroll_to_top")}>
					<KeyboardArrowUpIcon />
				</Fab>
			</ScrollToTop>
			{getDebug() ?
				<Box
					role="presentation"
					sx={{ position: 'fixed', bottom: 16, left: 16 }}
				>
					<Fab size="small" aria-label={t("aria.scroll_to_top")} onClick={() => {
						if (tolgee.getLanguage() === "de")
							tolgee.changeLanguage("en")
						else
							tolgee.changeLanguage("de");
					}}>
						<TranslateIcon />
					</Fab>
				</Box>
				:
				<React.Fragment></React.Fragment>
			}

		</React.Fragment>
	);

}

export default FinalThesisApp;